<template>
  <div>
    <Form
      class="w-full p-3 flex flex-col md:flex-row justify-between items-center gap-2 rounded-lg border-b-[16px] border-primary bg-white"
      v-slot="{ values, setFieldValue }"
      @submit="(values) => onSubmit(values, resetForm)"
    >
      <div
        class="w-full md:w-2/12 px-1 md:px-5 py-2 flex justify-start md:justify-center items-center gap-x-2"
      >
        <i class="ica-custom-fonts-search-file text-primary-color text-3xl text-primary-700" />
        <span class="text-primary text-sm">{{ $t('quickSearch') }}</span>
      </div>
      <div class="flex-1 w-full">
        <Field
          :rules="requiredRule"
          validate-on-input
          validate-on-change
          validate-on-blur
          name="requestDraftNumber"
          v-slot="{ field, errors: errs }"
        >
          <TextInput
            :field="field"
            :errors="errs"
            :placeholder="$t('RequestNumberOrPranOrIdNumber')"
            wrapper-class="!border-l-0 !border-r-0 !border-t-0 !border-b border-brand-grayblue-300"
            input-class="!border-none"
            mask="numberOnly"
            maxLength="25"
            label-class="w-full"
            class="pt-1 w-full"
            direction="flex-col"
          />
        </Field>
      </div>
      <GRecaptcha
        v-if="values.requestDraftNumber?.trim().length > 0"
        ref="gRecaptchaRef"
        @successCallback="(args) => setFieldValue('recaptchaResponse', args)"
      ></GRecaptcha>
      <button
        :disabled="!values.recaptchaResponse"
        class="px-3 py-1 w-full md:w-min text-center rounded-md bg-primary/80 hover:bg-primary hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
      >
        {{ $t('Inquiry') }}
      </button>
    </Form>
    <Modal :title="$t('quickSearch')" v-model="modal" container-class="w-11/12 md:w-3/5">
      <template #body>
        <HomeQuicksearchResult :result="data" />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import axiosInstance from '@/services'
import { Field, Form } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { notify } from 'notiwind'
import { ref, reactive } from 'vue'
import Modal from '@/components/Ui/Modal/index.vue'
import HomeQuicksearchResult from '@/views/home/HomeQuicksearchResult.vue'
import client_lookups from '@/assets/json/clientLookups.json'
import { useLookupsStore } from '@/stores/lookups'

const i18n = useI18n()
const modal = ref(false)

const lookupsStore = useLookupsStore()

const data = reactive({
  requestEntity: null,
  applicationHistory: null,
  isApproved: false,
  appointmentCollection: [],
  appointmentsEmirate: null,
  serviceCenters: [],
  emirates: [],
  appointmentStatuses: []
})

const {
  requestActions: { approved: requestActionApproved }
} = client_lookups

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

async function onSubmit(form) {
  const { requestDraftNumber } = form
  const validStartsWith = ['800', '784', '01']
  if (validStartsWith.some((v) => requestDraftNumber.startsWith(v)) === false) {
    notify(
      {
        group: 'info',
        text: i18n.t('checkNumber')
      },
      6000
    )

    return
  }

  const body = {
    ...form,
    isValidCaptcha: true,
    requestType: 1
  }

  const response = await axiosInstance.post('/api/landing/getRequestInfoQuickSearch', body)

  if (!response.requestDratftDto) {
    notify(
      {
        group: 'info',
        title: i18n.t('noDataFound'),
        text: i18n.t('checkNumber')
      },
      6000
    )

    return
  }

  data.requestEntity = response.requestDratftDto

  const { serviceTransId, requestNumber } = response.requestDratftDto

  if ([476, 478, 545, 479].includes(serviceTransId)) return

  if (requestNumber) {
    data.applicationHistory = response.requestActions
    data.isApproved = response.requestActions.some((x) => x.actionId == requestActionApproved)

    const {
      appointmentsOutput: {
        data: { items: appointments }
      }
    } = response

    if ((appointments ?? []).length > 0) {
      data.appointmentCollection = appointments

      await fetchRelatedData()
    }
  }

  modal.value = true
}

async function fetchRelatedData() {
  const [firstApp] = data.appointmentCollection
  const paths = [
    `/api/lookup/appointmentEmirateByAdminRegionId/${firstApp.adminRegionId}`,
    '/api/lookup/getServiceCenters',
    '/api/lookup/emirates',
    '/lookup/appointmentStatus'
  ]

  const [appointmentsEmirate, serviceCenters, emirates, appointmentStatuses] = await Promise.all(
    paths.map(async (path) => await lookupsStore.fetchLookup(path))
  )

  data.appointmentsEmirate = appointmentsEmirate
  data.serviceCenters = serviceCenters
  data.emirates = emirates
  data.appointmentStatuses = appointmentStatuses
}
</script>

<style lang="scss" scoped></style>
